import { Component, OnChanges, OnDestroy, OnInit, Input } from '@angular/core';

import { Subscription } from 'rxjs';

import { CostCenter } from 'src/shared/models/cost-center.model';
import { CostCenterConfigurationService } from 'src/shared/services/cost-center-configuration.service';
import { ShiftSchedule } from 'src/shared/models/shift-schedule.model';
import { ShiftScheduleService } from 'src/shared/services/shift-schedule.service';
import { StaffingGridConstants, StaffingGridType, StaffingGridConfiguration } from './utilities/staffing-grid-constants.util';
import { WorkflowPageUtil } from 'src/app/workflow/base-classes/workflow-page/workflow-page.util';
import { MiscUtil } from 'src/shared/utilities/misc.util';
import { StaffingGridUtil } from './utilities/staffing-grid.util';
import { UnitsOfServiceService } from 'src/shared/services/units-of-service.service';

@Component({
    selector: 'smt-staffing-grid',
    templateUrl: './staffing-grid.component.html',
    styleUrls: ['./staffing-grid.component.scss']
})
export class StaffingGridComponent implements OnDestroy, OnInit, OnChanges {
    @Input() costCenter: CostCenter;
    @Input() readOnly: boolean;
    @Input() isStaffingGridSaved: boolean;

    private costCenterSelectedSubscription: Subscription;
    private gridButtonPressedSubscription: Subscription;
    private gridServiceSubscription: Subscription;
    private saveSubscription: Subscription;

    readonly gridFormTypeAcuity = StaffingGridConfiguration.Acuity;
    readonly gridFormTypeInpatient = StaffingGridConfiguration.Inpatient;
    readonly gridFormTypeTotalUos = StaffingGridConfiguration.TotalUos;   

    selectedGridFormType: StaffingGridConfiguration;
    selectedGridType: string;
    selectedShiftBlockHour: number = null;
    schedules: ShiftSchedule[];
    unitsOfServiceCategory = "";

    constructor(private _costCenterConfigurationService: CostCenterConfigurationService,
        private _shiftScheduleService: ShiftScheduleService,
        private _unitsOfServiceService: UnitsOfServiceService) {
        this.gridButtonPressedSubscription = this._costCenterConfigurationService.gridButtonPressed$.subscribe((gridType) => {
            this.setGridType(gridType);
        });
        this._costCenterConfigurationService.isFutureShiftRecordPresent$.subscribe(futureShiftRecordFlag=>{
            if(futureShiftRecordFlag){
                this._costCenterConfigurationService.announceWarning(this._costCenterConfigurationService.staffingGridLockOutMessage);
                }
            else{
                this._costCenterConfigurationService.clearWarning();
            }
        });
        this.costCenterSelectedSubscription = this._costCenterConfigurationService.selected$.subscribe(
            (selectedCostCenter) => {                
                if (!this.costCenter || (selectedCostCenter && this.costCenter.id != selectedCostCenter.id)) {
                    this.costCenter = selectedCostCenter;
                    this.setStaffingGridType();
                }                
            });

        this.gridServiceSubscription = this._shiftScheduleService.schedules$.subscribe((schedules) => {
            this.schedules = schedules;
        });

        this.saveSubscription = this._costCenterConfigurationService.save$.subscribe(
            (tab) => {
                if (tab === "staffingGrid") {
                    this.saveStaffingGrid().then(() => {
                        this._costCenterConfigurationService.announceAdminListReload(false);
                    });
                }
            }
        );
    }

    ngOnChanges() {
        if (WorkflowPageUtil.hasUosConfiguration(this.costCenter)) {
            this._unitsOfServiceService.GetUnitsOfServiceById(this.costCenter.ancillaryConfiguration.unitOfServiceId).then((unitsOfService) => {
                this.unitsOfServiceCategory = unitsOfService.category; 
            });
        }
    }  

    ngOnDestroy() {
        if (this.costCenterSelectedSubscription) {
            this.costCenterSelectedSubscription.unsubscribe();
        }

        if (this.gridServiceSubscription) {
            this.gridServiceSubscription.unsubscribe();
        }

        if (this.saveSubscription) {
            this.saveSubscription.unsubscribe();
        }
    }

    ngOnInit() {
        this.loadShiftSchedules();
        this.setStaffingGridFormType();
        this.setStaffingGridType();
    }

    private createDefaultInpatientSchedules(): ShiftSchedule[] {
        let schedules: ShiftSchedule[] = [];

        schedules.push(this.createInpatientSchedule(true));
        schedules.push(this.createInpatientSchedule(false));

        return schedules;
    }

    private createInpatientSchedule(isWeekday: boolean): ShiftSchedule {
        let newShiftSchedule = new ShiftSchedule();

        newShiftSchedule.costCenterId = this.costCenter.id;
        newShiftSchedule.createdDate = new Date();
        newShiftSchedule.blockLength = StaffingGridConstants.DefaultShiftBlockLength;
        newShiftSchedule.partialShiftBlocks = [];
        newShiftSchedule.shiftBlocks = null;
        newShiftSchedule.startHour = this.selectedShiftBlockHour;
        newShiftSchedule.isSaturday = false;
        newShiftSchedule.isSunday = false;
        newShiftSchedule.isWeekday = isWeekday;
        newShiftSchedule.isWeekend = !isWeekday;

        StaffingGridUtil.setPartialShiftBlocks(newShiftSchedule);

        return newShiftSchedule;
    }

    getGridLabel(): string {
        let gridLabel = "";
        if (this.isAllWeekVariableSelected() || this.isAllWeekFixedSelected() || this.isAllWeekAddOnsSelected()) {
            gridLabel = StaffingGridConstants.AllWeekType.description;
        }
        else if (this.isSaturdayVariableSelected() || this.isSaturdayFixedSelected() || this.isSaturdayAddOnsSelected()) {
            gridLabel = StaffingGridConstants.SaturdayOnlyType.description;
        }
        else if (this.isSundayVariableSelected() || this.isSundayFixedSelected() || this.isSundayAddOnsSelected()) {
            gridLabel = StaffingGridConstants.SundayOnlyType.description;
        }
        else if (this.isWeekdayVariableSelected() || this.isWeekdayFixedSelected() || this.isWeekdayAddOnsSelected()) {
            gridLabel = StaffingGridConstants.WeekdayType.description;
        }
        else if (this.isWeekendVariableSelected() || this.isWeekendFixedSelected() || this.isWeekendAddOnsSelected()) {
            gridLabel = StaffingGridConstants.WeekendType.description;
        }

        if (this.unitsOfServiceCategory && gridLabel != "") {
            gridLabel += ` ${this.unitsOfServiceCategory}`;
        }

        return gridLabel;
    }

    isAddOnsSelected(): boolean {
        return this.isAllWeekAddOnsSelected() || 
            this.isSaturdayAddOnsSelected() || 
            this.isSundayAddOnsSelected() || 
            this.isWeekdayAddOnsSelected() ||
            this.isWeekendAddOnsSelected();
    }

    isAllWeekAddOnsSelected(): boolean {
        return this.selectedGridType === StaffingGridConstants.AllWeekAddOns;
    }

    isAllWeekFixedSelected(): boolean {
        return this.selectedGridType === StaffingGridConstants.AllWeekFixed;
    }

    isAllWeekVariableSelected(): boolean {
        return this.selectedGridType === StaffingGridConstants.AllWeekVariable;
    }

    isFixedSelected(): boolean {
        return this.isAllWeekFixedSelected() ||
            this.isSaturdayFixedSelected() ||
            this.isSundayFixedSelected() ||
            this.isWeekdayFixedSelected() ||
            this.isWeekendFixedSelected();
    }

    isSaturdayAddOnsSelected(): boolean {
        return this.selectedGridType === StaffingGridConstants.SaturdayAddOns;
    }

    isSaturdayFixedSelected(): boolean {
        return this.selectedGridType === StaffingGridConstants.SaturdayFixed;
    }

    isSaturdayVariableSelected() : boolean {
        return this.selectedGridType === StaffingGridConstants.SaturdayVariable;
    }

    isSundayAddOnsSelected(): boolean {
        return this.selectedGridType === StaffingGridConstants.SundayAddOns;
    }

    isSundayFixedSelected(): boolean {
        return this.selectedGridType === StaffingGridConstants.SundayFixed;
    }

    isSundayVariableSelected(): boolean {
        return this.selectedGridType === StaffingGridConstants.SundayVariable;
    }

    isVariableSelected(): boolean {
        return this.isAllWeekVariableSelected() ||
            this.isSaturdayVariableSelected() ||
            this.isSundayVariableSelected() ||
            this.isWeekdayVariableSelected() ||
            this.isWeekendVariableSelected();
    }

    isWeekdayAddOnsSelected(): boolean {
        return this.selectedGridType === StaffingGridConstants.WeekdayAddOns;
    }

    isWeekdayFixedSelected(): boolean {
        return this.selectedGridType === StaffingGridConstants.WeekdayFixed;
    }    

    isWeekdayVariableSelected(): boolean {
        return this.selectedGridType === StaffingGridConstants.WeekdayVariable;
    }

    isWeekendAddOnsSelected(): boolean {
        return this.selectedGridType === StaffingGridConstants.WeekendAddOns;
    }

    isWeekendFixedSelected(): boolean {
        return this.selectedGridType === StaffingGridConstants.WeekendFixed;
    }
        
    isWeekendVariableSelected(): boolean {
        return this.selectedGridType === StaffingGridConstants.WeekendVariable;
    }

    private async loadShiftSchedules() {  
        this._costCenterConfigurationService.announceAdminActionLoading(true);

        this.schedules = await this._shiftScheduleService.getActiveShiftSchedulesByCostCenter(this.costCenter.id);

        if (!MiscUtil.checkArray(this.schedules) && this.selectedGridFormType === this.gridFormTypeInpatient) {
            this.schedules = this.createDefaultInpatientSchedules();
        }

        if (MiscUtil.checkArray(this.schedules)) {
            this.selectedShiftBlockHour = this.schedules.sort(s => s.startHour)[0].startHour;
            this._shiftScheduleService.announceSchedules(this.schedules); 
        }             
        
        this._costCenterConfigurationService.announceAdminActionLoading(false);
    }     

    onChangeStartHour(selectedStartHour: number) {
        this.selectedShiftBlockHour = selectedStartHour;
        this.loadShiftSchedules();
    }

    async saveStaffingGrid() {
        let schedules = [];      

        if (MiscUtil.checkArray(this.schedules)) {                        
            schedules = this.schedules;
        }

        await this._shiftScheduleService.setShiftSchedules(schedules).then(
            async () => {
                await this.loadShiftSchedules();
                this._costCenterConfigurationService.setSelected(this.costCenter);
                this._costCenterConfigurationService.announceSaving(false);
                this._costCenterConfigurationService.announceFormIsDirty(false);
            },
            (error) => {
                this._costCenterConfigurationService.announceError("Staffing grid failed to save.");
                console.error(error);
            }
        );
    }

    setGridType(gridType: StaffingGridType) { 
        let gridButtonPressed = '';

        switch(gridType) {
            case StaffingGridType.AllWeekAddOns:
                gridButtonPressed = StaffingGridConstants.AllWeekAddOns;
                break;
            case StaffingGridType.AllWeekFixed:
                gridButtonPressed = StaffingGridConstants.AllWeekFixed;
                break;
            case StaffingGridType.AllWeekVariable:
                gridButtonPressed = StaffingGridConstants.AllWeekVariable;
                break;
            case StaffingGridType.SaturdayAddOns:
                gridButtonPressed = StaffingGridConstants.SaturdayAddOns;
                break;
            case StaffingGridType.SaturdayFixed:
                gridButtonPressed = StaffingGridConstants.SaturdayFixed;
                break;
            case StaffingGridType.SaturdayVariable:
                gridButtonPressed = StaffingGridConstants.SaturdayVariable;
                break;
            case StaffingGridType.SundayAddOns:
                gridButtonPressed = StaffingGridConstants.SundayAddOns;
                break;
            case StaffingGridType.SundayFixed:
                gridButtonPressed = StaffingGridConstants.SundayFixed;
                break;
            case StaffingGridType.SundayVariable:
                gridButtonPressed = StaffingGridConstants.SundayVariable;
                break;
            case StaffingGridType.WeekdayAddOns:
                gridButtonPressed = StaffingGridConstants.WeekdayAddOns;
                break;
            case StaffingGridType.WeekdayFixed:
                gridButtonPressed = StaffingGridConstants.WeekdayFixed;
                break;
            case StaffingGridType.WeekdayVariable:
                gridButtonPressed = StaffingGridConstants.WeekdayVariable;
                break;
            case StaffingGridType.WeekendAddOns:
                gridButtonPressed = StaffingGridConstants.WeekendAddOns;
                break;
            case StaffingGridType.WeekendFixed:
                gridButtonPressed = StaffingGridConstants.WeekendFixed;
                break;
            case StaffingGridType.WeekendVariable:
                gridButtonPressed = StaffingGridConstants.WeekendVariable;
                break;
        }

        this.selectedGridType = gridButtonPressed;
    } 

    private setStaffingGridFormType() {
        if (WorkflowPageUtil.hasAcuityConfiguration(this.costCenter) ||
            WorkflowPageUtil.hasAcuityWithRoomsConfiguration(this.costCenter)) {
            this.selectedGridFormType = this.gridFormTypeAcuity;
        }
        else if (WorkflowPageUtil.hasInpatientConfiguration(this.costCenter)) {
            this.selectedGridFormType = this.gridFormTypeInpatient;
        }
        else if (WorkflowPageUtil.hasUosConfiguration(this.costCenter)) {
            this.selectedGridFormType = this.gridFormTypeTotalUos;
        }
    }

    private setStaffingGridType(gridType: string = null): boolean {
        this.selectedGridType = gridType;
        return false;
    }
}
